import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastType = 'info' | 'success' | 'error';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

const MAX_TOASTS = 3;
const DURATIONS: Record<ToastType, number> = {
  info: 3000,
  success: 3000,
  error: 5000,
};

interface Ctx {
  toasts: Toast[];
  show: (msg: string, type?: ToastType) => void;
  dismiss: (id: string) => void;
}

const ToastCtx = createContext<Ctx>({ toasts: [], show: () => {}, dismiss: () => {} });
export const useToast = () => useContext(ToastCtx);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(p => p.filter(t => t.id !== id));
  }, []);

  const show = useCallback((message: string, type: ToastType = 'info') => {
    const id = `toast_${++counter}`;
    setToasts(p => {
      const next = [...p, { id, message, type }];
      // Evict oldest if over max
      if (next.length > MAX_TOASTS) return next.slice(next.length - MAX_TOASTS);
      return next;
    });
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), DURATIONS[type]);
  }, []);

  return (
    <ToastCtx.Provider value={{ toasts, show, dismiss }}>
      {children}
    </ToastCtx.Provider>
  );
}
